import type { LucideIcon } from 'lucide-react'

/**
 * The app's only icon call site — issue #190.
 *
 * ## Why this wrapper exists
 *
 * Before this, the sidebar rendered `item.label.slice(0, 1)` — the first
 * letter of the label. `shell.css` records what that cost: "the initials
 * collide (Categories/Connections, Review/Recurring/Reports) and a
 * screen-reader user hearing 'R' three times has lost the nav". The collision
 * was the missing icon set showing through, and the 68px rail was a column of
 * ambiguous letters.
 *
 * ## Why a library, in a repo that hand-rolls its charts
 *
 * CLAUDE.md bans a chart library, and the reasoning does not carry across. A
 * chart encodes data under contrast and legend rules this app defines; an icon
 * is a fixed glyph. The design's icons are drawn on a 24 grid with round caps
 * and joins at stroke-width 1.8 — Feather/Lucide geometry — so the choice was
 * between that family and 28 hand-authored path strings that would look like
 * 28 hand-authored path strings.
 *
 * The design's own `n.icon` path data is NOT available to copy: it lives in
 * the mock-data class in the truncated tail of the source (#198). So the
 * icons had to be chosen by name whichever route was taken.
 *
 * `lucide-react` ships no stylesheet — which matters, because
 * `controlTokens.test.ts` bans importing CSS from outside `src/` — and has
 * react as its only peer dependency. Named imports tree-shake, so the bundle
 * carries the icons used and not the set.
 *
 * ## Why everything goes through here
 *
 * One place sets the size and stroke, so "the icons are 1.8" is a fact about
 * one file rather than a convention 28 call sites have to remember. It is also
 * what makes the library swappable: if the real path data ever arrives with
 * #198, this is the only file that changes.
 *
 * ## Colour
 *
 * Lucide defaults `stroke` to `currentColor`, and this deliberately does not
 * override it. An icon inherits the ink of whatever it sits in — which is how
 * it stays correct on a lime `.hero-tile` and on `--surface` without either
 * caller knowing, and why no colour value appears in this file.
 */

/** The design's grid: 17px rendered from a 24 viewBox, 1.8 stroke. */
const SIZE = 17
const STROKE = 1.8

type Props = {
  /** A named import from `lucide-react` — `<Icon glyph={Wallet} />`. */
  glyph: LucideIcon
  /**
   * Icons in this app are decorative by default: every one of them sits beside
   * a text label that already carries the accessible name, and announcing both
   * reads the destination twice.
   *
   * Pass a label ONLY where the icon is the control's whole content — the top
   * bar's appearance toggle, for instance. Then it becomes an `img` role with
   * that name, rather than being hidden.
   */
  label?: string
  /** Overrides the default only where a control genuinely needs a larger mark. */
  size?: number
}

export function Icon({ glyph: Glyph, label, size = SIZE }: Props) {
  return (
    <Glyph
      width={size}
      height={size}
      strokeWidth={STROKE}
      // Lucide sets these already; restated because they are the two
      // properties that make the set read as one family, and a future default
      // change upstream should not silently reshape every icon in the app.
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      // `flex: none` because every icon in this design sits in a flex row
      // beside a label that may wrap. Without it the glyph is the thing that
      // squashes, and a 12px-wide icon is worse than no icon.
      style={{ flex: 'none' }}
    />
  )
}
